import { useCallback, useMemo } from "react";
import useFeature from "../../../../../hooks/useFeature";
import { currValidationFieldName, view } from "../../../../../utils/API";
import SituationalDateStatusIcon from "../../../../Dashboard/Editor/SituationalDateStatusIcon";
import { CloseIcon, EditIcon } from "../../../../Icons";
import { ShowPublicIcon } from "../../../../SharedComponents";
import { ReportTag } from "../../../../SharedInformation/SharedInformationCard/SharedInformationCard-styled";
import { FeatureHeadingWrapper, FeatureTitle } from "./FeatureHeading-styled";

const ShareIcon = ({ ...props }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M18.377 22C17.5436 22 16.8353 21.7083 16.252 21.125C15.6686 20.5417 15.377 19.8333 15.377 19C15.377 18.8833 15.3853 18.7623 15.402 18.637C15.4186 18.5117 15.4436 18.3993 15.477 18.3L8.42695 14.2C8.14362 14.45 7.82695 14.646 7.47695 14.788C7.12695 14.93 6.76029 15.0007 6.37695 15C5.54362 15 4.83529 14.7083 4.25195 14.125C3.66862 13.5417 3.37695 12.8333 3.37695 12C3.37695 11.1667 3.66862 10.4583 4.25195 9.875C4.83529 9.29167 5.54362 9 6.37695 9C6.76029 9 7.12695 9.071 7.47695 9.213C7.82695 9.355 8.14362 9.55067 8.42695 9.8L15.477 5.7C15.4436 5.6 15.4186 5.48767 15.402 5.363C15.3853 5.23833 15.377 5.11733 15.377 5C15.377 4.16667 15.6686 3.45833 16.252 2.875C16.8353 2.29167 17.5436 2 18.377 2C19.2103 2 19.9186 2.29167 20.502 2.875C21.0853 3.45833 21.377 4.16667 21.377 5C21.377 5.83333 21.0853 6.54167 20.502 7.125C19.9186 7.70833 19.2103 8 18.377 8C17.9936 8 17.627 7.92933 17.277 7.788C16.927 7.64667 16.6103 7.45067 16.327 7.2L9.27695 11.3C9.31029 11.4 9.33529 11.5127 9.35195 11.638C9.36862 11.7633 9.37695 11.884 9.37695 12C9.37695 12.116 9.36862 12.237 9.35195 12.363C9.33529 12.489 9.31029 12.6013 9.27695 12.7L16.327 16.8C16.6103 16.55 16.927 16.3543 17.277 16.213C17.627 16.0717 17.9936 16.0007 18.377 16C19.2103 16 19.9186 16.2917 20.502 16.875C21.0853 17.4583 21.377 18.1667 21.377 19C21.377 19.8333 21.0853 20.5417 20.502 21.125C19.9186 21.7083 19.2103 22 18.377 22Z"
      fill="#8C9BA5"
    />
  </svg>
);

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
};

const sizes = {
  sm: 18,
  md: 28,
  lg: 38,
};

const imageSizes = {
  sm: 18,
  md: 28,
  lg: 38,
};

const FeatureHeading = ({
  feature,
  t,
  handleShare,
  actionButtons,
  expanded,
  isPopup = false,
  hasClose = true,
  onEdit,
  size = "lg",
  cycleUpdateStatus,
  showPublic = false,
  i18n = null,
  symbologyFields = [],
}) => {
  const { getImage, title } = useFeature(feature, t, i18n);

  const handleClose = useCallback(() => {
    view.closePopup();
  }, []);

  const handleEdit = useCallback(
    (e) => {
      const editIndex = actionButtons.findIndex((b) => b.id === "edit-action");
      if (onEdit) {
        onEdit(e, editIndex);
      } else {
        if (editIndex > -1) {
          view.popup.triggerAction(editIndex);
        }
      }
    },
    [actionButtons]
  );

  const image = useMemo(() => {
    return getImage();
  }, [getImage]);

  const subtractWidth = useMemo(() => {
    let padding = 4;

    const hasEdit = actionButtons.some((b) => b.id === "edit-action");
    const hasShare = actionButtons.some((btn) => btn.id === "share");

    if (hasClose) {
      padding += sizes[size] - 2;
    }

    if (hasEdit) {
      padding += sizes[size] - 2;
    }

    if (hasShare) {
      padding += sizes[size] - 2;
    }
    return padding;
  }, [actionButtons, hasClose]);

  const tagsArray = useMemo(() => {
    const featureTags = [];
    if (Array.isArray(symbologyFields)) {
      symbologyFields
        .filter((item) => item.field === currValidationFieldName)
        .map((field) => {
          featureTags.push({
            title: field.value,
            color: field.bg,
          });
        });
    }

    return featureTags;
  }, [symbologyFields]);

  const isArabic = useMemo(() => {
    return i18n?.language === "ar";
  }, [i18n?.language]);

  return (
    <FeatureHeadingWrapper>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: size === "sm" ? 6 : 4,
            flex: 1,
            maxWidth: `calc(100% - ${subtractWidth}px)`,
          }}
        >
          {image && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: cycleUpdateStatus ? 34 : imageSizes[size],
                minWidth: cycleUpdateStatus ? 34 : imageSizes[size],
                position: "relative",
              }}
            >
              {getImage()}
            </div>
          )}
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FeatureTitle
              size={iconSizes[size]}
              isPopup={isPopup}
              hasUpdate={true}
            >
              <p
                style={{
                  maxWidth: `calc(100% - ${showPublic ? "16px" : "0px"})`,
                }}
                className={`${!expanded || isPopup ? "text-elipsis" : ""}`}
              >
                {title}
              </p>
              {showPublic && (
                <ShowPublicIcon
                  iconProps={{ width: 14, height: 14 }}
                  feature={feature}
                />
              )}
              {tagsArray.map((tag) => (
                <ReportTag
                  dir={isArabic ? "rtl" : "ltr"}
                  key={tag.title}
                  bg={tag.color}
                >
                  <span>
                    {tag.count}&nbsp;{tag.title}
                  </span>
                </ReportTag>
              ))}
            </FeatureTitle>
            {cycleUpdateStatus && (
              <SituationalDateStatusIcon
                feature={feature}
                showTitle
                filterShowPublic={false}
              />
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: sizes[size],
          }}
        >
          {actionButtons.some((btn) => btn.id === "edit-action") && (
            <div
              title={t("screen.popup.actions.edit-action")}
              style={{
                borderRadius: 100,
                border: "1px solid #eee",
                width:
                  sizes[size] - (size !== "lg" ? (size === "md" ? 6 : -2) : 10),
                height:
                  sizes[size] - (size !== "lg" ? (size === "md" ? 6 : -2) : 10),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={handleEdit}
            >
              <EditIcon
                width={iconSizes[size] - 2}
                height={iconSizes[size] - 2}
              />
            </div>
          )}
          {actionButtons.some((btn) => btn.id === "share") && (
            <div
              title={t("screen.popup.actions.share")}
              style={{
                borderRadius: 100,
                border: "1px solid #eee",
                width: sizes[size] - (size === "md" ? 6 : 10),
                height: sizes[size] - (size === "md" ? 6 : 10),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={handleShare}
            >
              <ShareIcon width={iconSizes[size]} height={iconSizes[size]} />
            </div>
          )}
          {hasClose && (
            <div
              title={t("screen.popup.actions.close")}
              style={{
                borderRadius: 100,
                border: "1px solid #eee",
                width: sizes[size] - (size === "md" ? 6 : 10),
                height: sizes[size] - (size === "md" ? 6 : 10),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={handleClose}
            >
              <CloseIcon width={iconSizes[size]} height={iconSizes[size]} />
            </div>
          )}
        </div>
      </div>
    </FeatureHeadingWrapper>
  );
};

export default FeatureHeading;
